import { ExclusionOrderResponse, ExclusionType } from '@inplay/models';

export class ExclusionOrderModel {
  private readonly data: ExclusionOrderResponse;

  constructor(data: ExclusionOrderResponse) {
    this.data = data;
  }

  get id(): ExclusionOrderResponse['id'] {
    return this.data.id;
  }

  get reportNo(): ExclusionOrderResponse['id'] {
    return this.data.id.slice(-7).toUpperCase();
  }

  get exclusionTypeKey(): string {
    if (this.data.type === ExclusionType.SELF_EXCLUSION) {
      return 'exclusionOrder.typeSelfExclusion';
    }
    return 'exclusionOrder.typeVenueInitiated';
  }

  get isActive(): boolean {
    return new Date(this.data.endDate) >= new Date();
  }

  get expiresInDays(): number {
    const remaining: number = Math.ceil(
      (new Date(this.data.endDate).getTime() - Date.now()) /
        (1000 * 60 * 60 * 24),
    );
    return Math.max(0, remaining);
  }

  get expiresInLabel(): string {
    if (this.isActive) {
      return 'exclusionOrder.days';
    }
    return 'exclusionOrder.expired';
  }

  get playerFullName(): string {
    return `${this.data.player.firstName} ${this.data.player.lastName}`;
  }

  get issuedByFullName(): string {
    return `${this.data.issuedBy.firstName} ${this.data.issuedBy.lastName}`;
  }

  get venueName(): ExclusionOrderResponse['venueBranch']['name'] {
    return this.data.venueBranch.name;
  }

  get createdAtFormatted(): string {
    return new Date(this.data.createdAt).toLocaleString();
  }

  get startDateFormatted(): string {
    return new Date(this.data.startDate).toLocaleDateString();
  }

  get endDateFormatted(): string {
    return new Date(this.data.endDate).toLocaleDateString();
  }

  get durationLabel(): string {
    return `${this.endDateFormatted} (${this.data.durationDays} days)`;
  }

  get breachCount(): ExclusionOrderResponse['breachCount'] {
    return this.data.breachCount;
  }

  get reason(): ExclusionOrderResponse['reason'] {
    return this.data.reason;
  }

  get reentryConditions(): ExclusionOrderResponse['reentryConditions'] {
    return this.data.reentryConditions;
  }

  get gamblerConsent(): ExclusionOrderResponse['gamblerConsent'] {
    return this.data.gamblerConsent;
  }

  get gamblerConsentLabel(): string {
    if (this.data.gamblerConsent?.agreed) {
      return 'exclusionOrder.consentAgreed';
    }
    return 'exclusionOrder.consentNotAgreed';
  }

  get gamblerSignedAtFormatted(): string {
    if (this.data.gamblerConsent?.signedAt) {
      return new Date(this.data.gamblerConsent.signedAt).toLocaleString();
    }
    return '';
  }

  get expiresInDisplay(): string {
    if (this.isActive) {
      return `${this.expiresInDays}`;
    }
    return 'exclusionOrder.expired';
  }

  get playerDob(): ExclusionOrderResponse['player']['dob'] {
    return this.data.player.dob || '-';
  }

  get playerAddress(): ExclusionOrderResponse['player']['address'] {
    return this.data.player.address || '-';
  }
}
